// Admin Support-Tickets board — filter by company, create tickets on behalf of companies.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Plus, LifeBuoy, ChevronDown, ChevronRight, X, Search, ArrowUp, ArrowDown, Check, UserX, Activity } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { DueDatePicker } from '../tickets/DueDatePicker';
import { toast } from 'sonner';
import { ticketsApi, adminApi, productsApi } from '../../services/api';
import api from '../../services/api';
import { getApiError } from '../../utils/errorHelper';
import { TicketDetailView, StatusBadge } from '../tickets/TicketDetailView';
import ActivityFeedTab from './ActivityFeedTab';

// Format a YYYY-MM-DD date string in a compact human-friendly way.
// Returns { label, tone } where tone hints at overdue/soon/normal for colour.
// formatDueDate helper removed — DueDatePicker component owns date formatting now.

// Consistent colour for a given admin so their avatar reads the same on every row.
const AVATAR_TONES = [
  { bg: 'bg-emerald-500/25', text: 'text-emerald-200' },
  { bg: 'bg-sky-500/25', text: 'text-sky-200' },
  { bg: 'bg-amber-500/25', text: 'text-amber-200' },
  { bg: 'bg-violet-500/25', text: 'text-violet-200' },
  { bg: 'bg-rose-500/25', text: 'text-rose-200' },
  { bg: 'bg-teal-500/25', text: 'text-teal-200' },
];
function avatarTone(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}
function avatarInitials(name) {
  return String(name || '?').split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
}

// Inline assignee picker — themed Popover with search, avatars and unassign,
// so it lines up with the rest of the dark ops console (native <select> looked
// jarring against the emerald/slate palette).
const InlineAssignee = ({ ticket, admins, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter(a =>
      (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q)
    );
  }, [admins, query]);

  const commit = async (newId) => {
    if ((newId || null) === (ticket.assigned_admin_id || null)) { setOpen(false); return; }
    setSaving(true);
    try {
      await ticketsApi.assign(ticket.id, newId || null);
      toast.success(newId ? 'Assignee updated' : 'Unassigned');
      setOpen(false);
      setQuery('');
      onChanged();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to change assignee'));
    } finally {
      setSaving(false);
    }
  };

  const tone = ticket.assigned_admin_name ? avatarTone(ticket.assigned_admin_name) : null;

  return (
    <div onClick={e => e.stopPropagation()}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={saving}
            className="flex items-center gap-2 min-w-0 w-full text-left px-1.5 py-1 rounded-md hover:bg-emerald-500/10 transition-colors"
            data-testid={`admin-ticket-assign-trigger-${ticket.id}`}
          >
            {tone ? (
              <span className={`flex items-center justify-center w-6 h-6 rounded-full ${tone.bg} ${tone.text} text-[10px] font-bold flex-shrink-0`}>
                {avatarInitials(ticket.assigned_admin_name)}
              </span>
            ) : (
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-700/50 text-slate-500 text-[10px] flex-shrink-0">
                <UserX className="w-3 h-3" />
              </span>
            )}
            <span className={`text-xs truncate ${ticket.assigned_admin_name ? 'text-slate-200' : 'text-slate-500 italic'}`}>
              {saving ? 'Saving…' : (ticket.assigned_admin_name || 'Unassigned')}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 p-0 border-emerald-900/40 shadow-2xl"
          align="start"
          style={{ background: '#0a0f0d', color: '#e2e8f0' }}
          data-testid={`admin-ticket-assign-popover-${ticket.id}`}
        >
          <div className="relative border-b border-emerald-900/40">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search teammates…"
              className="w-full pl-8 pr-3 py-2 text-xs bg-transparent text-white placeholder-slate-500 focus:outline-none"
              data-testid={`admin-ticket-assign-search-${ticket.id}`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => commit(null)}
              className={`w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-emerald-500/10 ${!ticket.assigned_admin_id ? 'bg-emerald-500/5' : ''}`}
              data-testid={`admin-ticket-assign-option-unassign-${ticket.id}`}
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-700/40 text-slate-500 flex-shrink-0">
                <UserX className="w-3 h-3" />
              </span>
              <span className="text-xs text-slate-400 italic flex-1">Unassigned</span>
              {!ticket.assigned_admin_id && <Check className="w-3.5 h-3.5 text-emerald-400" />}
            </button>
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-[11px] text-slate-500 italic">No teammates match &ldquo;{query}&rdquo;.</p>
            ) : (
              filtered.map(a => {
                const isCurrent = a.id === ticket.assigned_admin_id;
                const t = avatarTone(a.name);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => commit(a.id)}
                    className={`w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-emerald-500/10 ${isCurrent ? 'bg-emerald-500/5' : ''}`}
                    data-testid={`admin-ticket-assign-option-${ticket.id}-${a.id}`}
                  >
                    <span className={`flex items-center justify-center w-6 h-6 rounded-full ${t.bg} ${t.text} text-[10px] font-bold flex-shrink-0`}>
                      {avatarInitials(a.name)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-200 truncate">{a.name}</div>
                      {a.email && <div className="text-[10px] text-slate-500 truncate">{a.email}</div>}
                    </div>
                    {isCurrent && <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// InlineDueDate replaced by the shared themed DueDatePicker component
// (see components/tickets/DueDatePicker.jsx) — a proper calendar popover with
// editable text input, month/year nav, and guaranteed dark theme.

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

// Sortable column header — moved out of the parent to avoid re-creating the
// component type on every render (React would tear down and remount otherwise).
const SortableHeader = ({ col, label, testid, sortBy, sortDir, onToggle }) => {
  const active = sortBy === col;
  return (
    <button
      type="button"
      onClick={() => onToggle(col)}
      className={`text-left text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 select-none hover:text-emerald-300 ${active ? 'text-emerald-300' : 'text-slate-500'}`}
      data-testid={testid}
    >
      {label}
      {active && (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
    </button>
  );
};

// Wrap every case-insensitive occurrence of `query` inside `text` with a <mark>.
// Returns a fragment safe to render — no dangerouslySetInnerHTML needed.
function Highlight({ text, query }) {
  const s = String(text ?? '');
  const q = String(query ?? '').trim();
  if (!q) return <>{s}</>;
  // Escape regex metachars in the query
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = s.split(new RegExp(`(${escaped})`, 'ig'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-emerald-400/25 text-emerald-100 rounded px-0.5" data-testid="search-highlight">{part}</mark>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
}

const STATUS_ORDER = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_LABEL = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };

const CreateTicketModal = ({ companies, onClose, onCreated }) => {
  const [form, setForm] = useState({ company_id: '', subject: '', description: '', order_id: '' });
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);

  const selectedCompany = companies.find(c => c.id === form.company_id);

  useEffect(() => {
    if (!form.company_id) { setOrders([]); return; }
    setLoadingOrders(true);
    ticketsApi.listCompanyOrders(form.company_id)
      .then(o => setOrders(o))
      .catch(() => setOrders([]))
      .finally(() => setLoadingOrders(false));
  }, [form.company_id]);

  const filteredCompanies = useMemo(() => {
    const q = companySearch.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(c => (c.company_name || '').toLowerCase().includes(q));
  }, [companies, companySearch]);

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(o => (o.product_name || '').toLowerCase().includes(q) || (o.period || '').toLowerCase().includes(q));
  }, [orders, orderSearch]);

  const canSubmit = form.company_id && form.subject.trim() && form.description.trim();

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        company_id: form.company_id,
        subject: form.subject.trim(),
        description: form.description.trim(),
      };
      if (form.order_id) payload.order_id = form.order_id;
      const t = await ticketsApi.create(payload);
      toast.success(`Ticket #${t.ticket_number} created`);
      onCreated(t.id);
    } catch (e) {
      toast.error(getApiError(e, 'Failed to create ticket'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-new-ticket-modal">
      <div className="bg-[#0f1611] border border-emerald-900/40 rounded-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Create Communication</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Company picker */}
        <div className="mb-3">
          <label className="block text-xs text-slate-400 mb-1">Company <span className="text-red-400">*</span></label>
          <button
            type="button"
            onClick={() => setCompanyPickerOpen(v => !v)}
            className="w-full bg-[#0a0f0d] border border-emerald-900/30 rounded-md px-3 py-2 text-sm text-white flex items-center justify-between hover:border-emerald-500"
            data-testid="admin-new-ticket-company-toggle"
          >
            <span>{selectedCompany?.company_name || 'Select company…'}</span>
            {companyPickerOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {companyPickerOpen && (
            <div className="mt-1 p-2 bg-[#0a0f0d] border border-emerald-900/30 rounded-md">
              <input
                type="text"
                placeholder="Search companies…"
                value={companySearch}
                onChange={e => setCompanySearch(e.target.value)}
                className="w-full bg-[#0f1611] border border-emerald-900/30 rounded px-2 py-1 text-xs text-white mb-1"
                data-testid="admin-new-ticket-company-search"
              />
              <div className="max-h-44 overflow-y-auto space-y-0.5">
                {filteredCompanies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setForm(f => ({ ...f, company_id: c.id, order_id: '' })); setCompanyPickerOpen(false); }}
                    className="w-full text-left px-2 py-1.5 text-xs text-slate-300 hover:bg-emerald-500/10 rounded"
                    data-testid={`admin-new-ticket-company-${c.id}`}
                  >
                    {c.company_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mb-3">
          <label className="block text-xs text-slate-400 mb-1">Subject <span className="text-red-400">*</span></label>
          <input
            type="text"
            value={form.subject}
            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            className="w-full bg-[#0a0f0d] border border-emerald-900/30 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
            data-testid="admin-new-ticket-subject"
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs text-slate-400 mb-1">Description <span className="text-red-400">*</span></label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={4}
            className="w-full bg-[#0a0f0d] border border-emerald-900/30 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500 resize-none"
            data-testid="admin-new-ticket-description"
          />
        </div>

        {/* Order picker (optional, enabled after company selected) */}
        <div className="mb-4">
          <label className="block text-xs text-slate-400 mb-1">Link to Order <span className="text-slate-600">(optional)</span></label>
          {!form.company_id ? (
            <p className="text-xs text-slate-500 italic">Pick a company first.</p>
          ) : loadingOrders ? (
            <p className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading orders…</p>
          ) : orders.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No orders for this company.</p>
          ) : (
            <>
              <input
                type="text"
                placeholder="Search orders…"
                value={orderSearch}
                onChange={e => setOrderSearch(e.target.value)}
                className="w-full bg-[#0a0f0d] border border-emerald-900/30 rounded px-2 py-1 text-xs text-white mb-1"
                data-testid="admin-new-ticket-order-search"
              />
              <div className="max-h-32 overflow-y-auto space-y-0.5 bg-[#0a0f0d] border border-emerald-900/30 rounded p-1">
                <button
                  onClick={() => setForm(f => ({ ...f, order_id: '' }))}
                  className={`w-full text-left px-2 py-1 text-xs rounded ${!form.order_id ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-300 hover:bg-emerald-500/10'}`}
                >
                  — None —
                </button>
                {filteredOrders.map(o => (
                  <button
                    key={o.id}
                    onClick={() => setForm(f => ({ ...f, order_id: o.id }))}
                    className={`w-full text-left px-2 py-1 text-xs rounded ${form.order_id === o.id ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-300 hover:bg-emerald-500/10'}`}
                    data-testid={`admin-new-ticket-order-${o.id}`}
                  >
                    {o.product_name} {o.period && <span className="text-slate-500">· {o.period}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="admin-btn admin-btn-ghost">Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="admin-btn"
            style={{ background: '#10b981', color: '#fff' }}
            data-testid="admin-new-ticket-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Communication'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const TicketsTab = ({ onNavigateToCompany, onNavigateToOrder, onNavigateOrderFromActivity } = {}) => {
  // 'threads' shows the existing tickets table. 'activity' shows the reverse-
  // chronological feed of everything that happened across orders + threads.
  const [subView, setSubView] = useState('threads');
  const [activityUnread, setActivityUnread] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [filterAssignee, setFilterAssignee] = useState(''); // '', 'me', or admin_id
  const [filterProductId, setFilterProductId] = useState('');
  const [filterNoOrder, setFilterNoOrder] = useState(false);
  const [products, setProducts] = useState([]);
  // Asana-style header sort: click cycles asc → desc → off. Applies within each
  // status group. When null, keeps API's updated_at DESC with "needs reply" floated to top.
  const [sortBy, setSortBy] = useState(null); // 'assignee' | 'due_date' | null
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [openTicketId, setOpenTicketId] = useState(null);
  const [closedExpanded, setClosedExpanded] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('');

  // Deep-link: ?ticket=<id> opens that ticket directly
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tId = params.get('ticket');
    if (tId) setOpenTicketId(tId);

    // Hydrate current user id from localStorage — the user is already logged in,
    // so there's no need to spend a round-trip on /auth/me just to power the
    // "Assigned to me" filter. This trims ~150-400ms off first paint.
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const u = JSON.parse(rawUser);
        setCurrentUserId(u?.id || u?.user_id || '');
      }
    } catch { /* ignore parse errors */ }
  }, []);

  // Companies + admins + products list rarely change during a tab session — fetch
  // them once on mount and reuse across filter changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, ad, pr] = await Promise.all([
          adminApi.getCompanies(),
          ticketsApi.listAdmins().catch(() => []),
          productsApi.getAllAdmin(false).catch(() => []),
        ]);
        if (!cancelled) {
          setCompanies(c);
          setAdmins(ad);
          setProducts(pr);
        }
      } catch (e) {
        if (!cancelled) toast.error(getApiError(e, 'Failed to load supporting data'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll activity feed unread count every 60s (spec §1.7). Cheap ping,
  // powers the Activity sub-tab badge above.
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      api.get('/activities/unread-count')
        .then(r => { if (!cancelled) setActivityUnread(r.data?.count || 0); })
        .catch(() => { /* silent — badge is decorative */ });
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);
  // Refresh badge whenever the user leaves the Activity sub-view — they may have
  // marked things read while it was open.
  useEffect(() => {
    if (subView !== 'activity') {
      api.get('/activities/unread-count')
        .then(r => setActivityUnread(r.data?.count || 0))
        .catch(() => { /* silent */ });
    }
  }, [subView]);

  const load = async (searchOverride) => {
    setLoading(true);
    try {
      const query = {};
      if (filterCompanyId) query.company_id = filterCompanyId;
      if (filterProductId) query.product_id = filterProductId;
      if (filterNoOrder) query.no_order = true;
      if (filterAssignee === 'me' && currentUserId) query.assigned_admin_id = currentUserId;
      else if (filterAssignee && filterAssignee !== 'me') query.assigned_admin_id = filterAssignee;
      // Server-side full-text search — matches subject, description, conversation_id,
      // activity/message text, company name and creator name (see /api/tickets).
      const q = (searchOverride !== undefined ? searchOverride : searchQuery).trim();
      if (q) query.q = q;

      const t = await ticketsApi.list(query);
      setTickets(t);
    } catch (e) {
      toast.error(getApiError(e, 'Failed to load tickets'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCompanyId, filterAssignee, filterProductId, filterNoOrder, currentUserId]);

  // Debounce searchQuery → server-side re-fetch so message bodies are also searched.
  useEffect(() => {
    const handle = setTimeout(() => { load(searchQuery); }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line
  }, [searchQuery]);

  const grouped = useMemo(() => {
    const g = { open: [], in_progress: [], resolved: [], closed: [] };
    // Tickets are already filtered by the server for search; group them directly.
    for (const t of tickets) if (g[t.status]) g[t.status].push(t);

    const cmpAssignee = (a, b) => {
      const av = (a.assigned_admin_name || '').toLowerCase();
      const bv = (b.assigned_admin_name || '').toLowerCase();
      // Empty always sinks to the bottom regardless of direction
      if (!av && bv) return 1;
      if (av && !bv) return -1;
      if (!av && !bv) return 0;
      return av.localeCompare(bv);
    };
    const cmpDueDate = (a, b) => {
      const av = a.due_date || '';
      const bv = b.due_date || '';
      if (!av && bv) return 1;
      if (av && !bv) return -1;
      if (!av && !bv) return 0;
      return av.localeCompare(bv);
    };

    for (const s of Object.keys(g)) {
      if (sortBy === 'assignee') {
        g[s].sort((a, b) => (sortDir === 'asc' ? cmpAssignee(a, b) : -cmpAssignee(a, b)));
      } else if (sortBy === 'due_date') {
        g[s].sort((a, b) => (sortDir === 'asc' ? cmpDueDate(a, b) : -cmpDueDate(a, b)));
      } else {
        // Default: float "needs reply" tickets to the top so admins land on them first.
        // Preserves the API's updated_at DESC ordering among peers.
        g[s].sort((a, b) => {
          if (a.pending_admin_reply === b.pending_admin_reply) return 0;
          return a.pending_admin_reply ? -1 : 1;
        });
      }
    }
    return g;
  }, [tickets, sortBy, sortDir]);

  const pendingCount = useMemo(
    () => tickets.filter(t => t.pending_admin_reply && t.status !== 'closed').length,
    [tickets]
  );

  // Companies with at least one ticket — for the filter dropdown
  const companiesWithTickets = useMemo(() => {
    const ids = new Set(tickets.map(t => t.company_id));
    // If no filter set, tickets already contains everything; if filter set, we need full company list
    const source = filterCompanyId ? companies : companies.filter(c => ids.has(c.id));
    return source.length ? source : companies;
  }, [tickets, companies, filterCompanyId]);

  // Assemble the list of currently-active filters so we can render removable chips.
  const activeFilters = useMemo(() => {
    const chips = [];
    if (searchQuery.trim()) chips.push({ key: 'q', label: `Search: "${searchQuery.trim()}"`, clear: () => setSearchQuery('') });
    if (filterAssignee === 'me') chips.push({ key: 'assignee-me', label: 'Assigned: me', clear: () => setFilterAssignee('') });
    else if (filterAssignee) {
      const admin = admins.find(a => a.id === filterAssignee);
      chips.push({ key: 'assignee', label: `Assigned: ${admin?.name || filterAssignee}`, clear: () => setFilterAssignee('') });
    }
    if (filterCompanyId) {
      const c = companies.find(x => x.id === filterCompanyId);
      chips.push({ key: 'company', label: `Company: ${c?.company_name || filterCompanyId}`, clear: () => setFilterCompanyId('') });
    }
    if (filterProductId) {
      const p = products.find(x => x.id === filterProductId);
      chips.push({ key: 'product', label: `Product: ${p?.name || filterProductId}`, clear: () => setFilterProductId('') });
    }
    if (filterNoOrder) chips.push({ key: 'no-order', label: 'No linked order', clear: () => setFilterNoOrder(false) });
    return chips;
  }, [searchQuery, filterAssignee, filterCompanyId, filterProductId, filterNoOrder, admins, companies, products]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterAssignee('');
    setFilterCompanyId('');
    setFilterProductId('');
    setFilterNoOrder(false);
  };

  // Cycle sort: clicking a header toggles asc → desc → off for that column;
  // clicking a different column jumps directly to asc on it.
  const toggleSort = (col) => {
    if (sortBy !== col) { setSortBy(col); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortBy(null); setSortDir('asc');
  };

  if (openTicketId) {
    return (
      <TicketDetailView
        ticketId={openTicketId}
        isAdmin={true}
        onBack={() => { setOpenTicketId(null); load(); }}
        onNavigateToCompany={onNavigateToCompany}
        onNavigateToOrder={onNavigateToOrder}
      />
    );
  }

  const renderRow = (t, idx) => (
    <div
      key={t.id}
      role="button"
      tabIndex={0}
      onClick={() => setOpenTicketId(t.id)}
      onKeyDown={e => { if (e.key === 'Enter') setOpenTicketId(t.id); }}
      className={`grid grid-cols-[36px_1fr_180px_120px] gap-4 items-center px-3 py-2.5 border-b border-emerald-900/20 hover:bg-emerald-500/5 cursor-pointer transition-colors ${
        t.pending_admin_reply ? 'bg-red-500/[0.04]' : ''
      }`}
      data-testid={`admin-ticket-row-${t.id}`}
    >
      {/* # */}
      <span className="text-xs text-slate-500 font-mono tabular-nums">{idx + 1}</span>

      {/* Name column: conversation id · subject · badges */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-slate-500 font-mono">
            <Highlight text={t.conversation_id || `#${t.ticket_number}`} query={searchQuery} />
          </span>
          <span className="font-semibold text-white truncate text-sm">
            <Highlight text={t.subject} query={searchQuery} />
          </span>
          <StatusBadge status={t.status} />
          {!t.order_id && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-semibold uppercase tracking-wider"
              title="This thread is standalone — not attached to any order"
              data-testid={`admin-ticket-no-order-badge-${t.id}`}
            >
              no order
            </span>
          )}
          {t.pending_admin_reply && (
            <span
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] font-semibold whitespace-nowrap"
              title="Client replied and is awaiting a response from the team"
              data-testid={`admin-ticket-pending-badge-${t.id}`}
            >
              <span className="w-1 h-1 rounded-full bg-red-400 animate-pulse" />
              Needs reply
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3">
          <span><Highlight text={t.company_name} query={searchQuery} /></span>
          <span>By <Highlight text={t.created_by?.name} query={searchQuery} /></span>
          <span>Updated {formatTime(t.last_activity_at)}</span>
        </div>
      </div>

      {/* Assignee — inline editable */}
      <InlineAssignee ticket={t} admins={admins} onChanged={load} />

      {/* Due date — inline editable */}
      {/* Due date — themed calendar popover (see DueDatePicker.jsx) */}
      <DueDatePicker
        value={t.due_date || null}
        compact
        testId={`admin-ticket-due-date-${t.id}`}
        onChange={async (iso) => {
          try {
            await ticketsApi.setDueDate(t.id, iso);
            toast.success(iso ? 'Due date updated' : 'Due date cleared');
            load();
          } catch (err) {
            toast.error(getApiError(err, 'Failed to update due date'));
          }
        }}
      />
    </div>
  );

  return (
    <div data-testid="admin-tickets-tab">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-emerald-400" /> Communication
            {pendingCount > 0 && subView === 'threads' && (
              <span
                className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-semibold"
                title={`${pendingCount} thread${pendingCount === 1 ? '' : 's'} waiting on a team reply`}
                data-testid="admin-tickets-pending-count"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                {pendingCount} needs reply
              </span>
            )}
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            {subView === 'threads'
              ? 'All communication threads. Each thread carries both client-facing replies and internal team notes.'
              : 'Everything that happened across your orders in one reverse-chronological feed (last 180 days).'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {subView === 'threads' && (
            <button
              onClick={() => setShowCreate(true)}
              className="admin-btn"
              style={{ background: '#10b981', color: '#fff' }}
              data-testid="admin-new-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Communication
            </button>
          )}
        </div>
      </div>

      {/* Sub-view switcher: Threads (tickets table) vs Activity Feed */}
      <div className="flex items-end gap-0 border-b border-emerald-900/30 mb-4" role="tablist" data-testid="communication-subtabs">
        <button
          role="tab"
          aria-selected={subView === 'threads'}
          onClick={() => setSubView('threads')}
          data-testid="communication-subtab-threads"
          className={`px-4 py-2.5 text-sm -mb-px border-b-2 flex items-center gap-2 ${subView === 'threads' ? 'border-emerald-500 text-white font-semibold' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
        >
          <LifeBuoy className="w-4 h-4" />
          Threads
        </button>
        <button
          role="tab"
          aria-selected={subView === 'activity'}
          onClick={() => setSubView('activity')}
          data-testid="communication-subtab-activity"
          className={`px-4 py-2.5 text-sm -mb-px border-b-2 flex items-center gap-2 ${subView === 'activity' ? 'border-emerald-500 text-white font-semibold' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
        >
          <Activity className="w-4 h-4" />
          Activity Feed
          {activityUnread > 0 && (
            <span
              data-testid="communication-subtab-activity-badge"
              className="inline-flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 min-w-[18px] h-[16px]"
            >
              {activityUnread > 99 ? '99+' : activityUnread}
            </span>
          )}
        </button>
      </div>

      {/* Activity Feed sub-view — nested inside Communication per user's ask. */}
      {subView === 'activity' && (
        <ActivityFeedTab onNavigateOrder={onNavigateOrderFromActivity} />
      )}

      {/* Threads sub-view (default) — original tickets table below. */}
      {subView === 'threads' && (
      <>

      {/* Search + filter toolbar — mirrors the Orders page toolbar for a consistent look */}
      <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="admin-tickets-toolbar">
        <div className="relative flex-1 min-w-[240px] max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by conversation ID, subject, company, message body…"
            className="w-full h-10 pl-10 pr-9 text-sm bg-[#0f1611] border border-emerald-900/40 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="admin-tickets-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              data-testid="admin-tickets-search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <select
          value={filterAssignee}
          onChange={e => setFilterAssignee(e.target.value)}
          className="h-10 px-3 text-sm bg-[#0f1611] border border-emerald-900/40 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="admin-tickets-assignee-filter"
        >
          <option value="">All Team Members</option>
          <option value="me">Assigned to me</option>
          <option disabled>──────────</option>
          {admins.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <select
          value={filterCompanyId}
          onChange={e => setFilterCompanyId(e.target.value)}
          className="h-10 px-3 text-sm bg-[#0f1611] border border-emerald-900/40 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="admin-tickets-company-filter"
        >
          <option value="">Showing: All Companies</option>
          {companiesWithTickets.map(c => (
            <option key={c.id} value={c.id}>{c.company_name}</option>
          ))}
        </select>
        <select
          value={filterProductId}
          onChange={e => setFilterProductId(e.target.value)}
          className="h-10 px-3 text-sm bg-[#0f1611] border border-emerald-900/40 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="admin-tickets-product-filter"
        >
          <option value="">All Products</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <label
          className={`h-10 flex items-center gap-2 px-3 text-sm rounded-lg border cursor-pointer select-none transition-colors ${
            filterNoOrder
              ? 'bg-amber-500/15 border-amber-500/50 text-amber-200'
              : 'bg-[#0f1611] border-emerald-900/40 text-slate-300 hover:border-emerald-500/50'
          }`}
          title="Show only threads that are NOT linked to any order — useful for triaging generic inquiries"
        >
          <input
            type="checkbox"
            checked={filterNoOrder}
            onChange={e => setFilterNoOrder(e.target.checked)}
            className="w-4 h-4 rounded accent-amber-500 cursor-pointer"
            data-testid="admin-tickets-no-order-filter"
          />
          No linked order
        </label>
      </div>

      {/* Active filter chips — one-click clear per filter, plus a "Clear all" affordance
          whenever two or more filters are active. Helps admins keep track of what's narrowing the view. */}
      {activeFilters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-5 -mt-2" data-testid="admin-tickets-filter-chips">
          <span className="text-[11px] text-slate-500 uppercase tracking-wider">Filters:</span>
          {activeFilters.map(chip => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[11px] font-medium"
              data-testid={`admin-tickets-filter-chip-${chip.key}`}
            >
              {chip.label}
              <button
                onClick={chip.clear}
                className="ml-0.5 -mr-0.5 rounded hover:bg-emerald-500/20 p-0.5"
                aria-label={`Remove ${chip.label} filter`}
                data-testid={`admin-tickets-filter-chip-clear-${chip.key}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {activeFilters.length > 1 && (
            <button
              onClick={clearAllFilters}
              className="text-[11px] text-slate-400 hover:text-white underline"
              data-testid="admin-tickets-filter-clear-all"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Sub-tabs removed — every thread now unifies User & Team replies with Team-only internal notes.
          Admins toggle visibility per message from the composer inside the thread. */}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
      ) : tickets.length === 0 && searchQuery.trim() ? (
        <div className="p-10 text-center bg-[#0f1611] border border-emerald-900/30 rounded-xl" data-testid="admin-tickets-empty-search">
          <Search className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No threads match &ldquo;{searchQuery}&rdquo; — we also checked every message body.</p>
          <button
            onClick={() => setSearchQuery('')}
            className="text-xs text-emerald-400 hover:text-emerald-300 mt-2 underline"
            data-testid="admin-tickets-empty-search-clear"
          >
            Clear search
          </button>
        </div>
      ) : tickets.length === 0 ? (
        <div className="p-10 text-center bg-[#0f1611] border border-emerald-900/30 rounded-xl" data-testid="admin-tickets-empty">
          <LifeBuoy className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No tickets{filterCompanyId ? ' for this company' : ''} yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {STATUS_ORDER.filter(s => s !== 'closed').map(s => (
            grouped[s].length > 0 && (
              <div key={s} data-testid={`admin-tickets-group-${s}`}>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 flex items-center gap-2">
                  <StatusBadge status={s} /> <span>({grouped[s].length})</span>
                </h3>
                <div className="bg-[#0f1611] border border-emerald-900/30 rounded-lg overflow-hidden">
                  {/* Table header — Asana-style column titles */}
                  <div className="grid grid-cols-[36px_1fr_180px_120px] gap-4 items-center px-3 py-2 border-b border-emerald-900/40 bg-[#0a0f0d]" data-testid={`admin-tickets-header-${s}`}>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">#</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Name</span>
                    <SortableHeader col="assignee" label="Assignee" testid="admin-tickets-sort-assignee" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                    <SortableHeader col="due_date" label="Due date" testid="admin-tickets-sort-due-date" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                  </div>
                  {grouped[s].map((t, i) => renderRow(t, i))}
                </div>
              </div>
            )
          ))}
          {grouped.closed.length > 0 && (
            <div data-testid="admin-tickets-group-closed">
              <button
                onClick={() => setClosedExpanded(v => !v)}
                className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 hover:text-slate-300"
                data-testid="admin-tickets-closed-toggle"
              >
                {closedExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <StatusBadge status="closed" /> <span>({grouped.closed.length})</span>
              </button>
              {closedExpanded && (
                <div className="bg-[#0f1611] border border-emerald-900/30 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[36px_1fr_180px_120px] gap-4 items-center px-3 py-2 border-b border-emerald-900/40 bg-[#0a0f0d]">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">#</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Name</span>
                    <SortableHeader col="assignee" label="Assignee" testid="admin-tickets-sort-assignee-closed" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                    <SortableHeader col="due_date" label="Due date" testid="admin-tickets-sort-due-date-closed" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} />
                  </div>
                  {grouped.closed.map((t, i) => renderRow(t, i))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <CreateTicketModal
          companies={companies}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); setOpenTicketId(id); load(); }}
        />
      )}
      </>
      )}
    </div>
  );
};

export default TicketsTab;
